import { useMemo } from 'react'

import {
  DataCollectionPaginatedFetchOptions,
  FiltersDefinition,
  GroupingDefinition,
  ItemActionsDefinition,
  NavigationFiltersDefinition,
  SortingsDefinition,
  SummariesDefinition,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { getMarketplaceGraphqlClient } from 'modules/it_management/lib/marketplaceGraphqlClient'
import { LIST_ORDERS } from 'modules/it_management/lib/marketplaceOrderGraphql'

export type OrderRecord = {
  id: string
  status: string
  currency: string
  price: string
  cartId: string
  createdAt: string | null
  itemsCount: number
  entity?: string
  type?: string
}

const PER_PAGE = 20

export function useOrdersDataCollectionSource() {
  const { company } = useCurrentSession()
  const marketplaceClient = getMarketplaceGraphqlClient()
  const companyId = Number(company.id)

  const source = useDataCollectionSource<
    OrderRecord,
    FiltersDefinition<string>,
    SortingsDefinition,
    SummariesDefinition,
    ItemActionsDefinition<OrderRecord>,
    NavigationFiltersDefinition<string>,
    GroupingDefinition<OrderRecord>
  >(
    useMemo(
      () => ({
        dataAdapter: {
          paginationType: 'pages' as const,
          perPage: PER_PAGE,
          fetchData: (
            options: DataCollectionPaginatedFetchOptions<
              FiltersDefinition<string>,
              NavigationFiltersDefinition<string>
            >
          ) => {
            const { search, pagination } = options
            const currentPage = pagination?.currentPage ?? 1
            const perPage = pagination?.perPage ?? PER_PAGE
            const normalizedSearch = search?.trim() || undefined

            return marketplaceClient
              .watchQuery({
                query: LIST_ORDERS,
                variables: { companyId, search: normalizedSearch },
                fetchPolicy: 'network-only',
                notifyOnNetworkStatusChange: false,
              })
              .map(({ data, loading, error }) => {
                const orders: OrderRecord[] = data?.orders ?? []
                const total = orders.length
                const start = (currentPage - 1) * perPage
                const records = orders.slice(start, start + perPage)

                return {
                  loading,
                  error: error ?? undefined,
                  data: {
                    records,
                    total,
                    type: 'pages' as const,
                    currentPage,
                    perPage,
                    pagesCount: Math.ceil(total / perPage) || 1,
                  },
                }
              })
          },
        },
        search: { enabled: true },
        filters: {
          entity: {
            type: 'in' as const,
            label: i18n.t('it_management.device_catalog.orders.filter_entities'),
            options: {
              options: [
                {
                  label: i18n.t('it_management.device_catalog.orders.filter_entities'),
                  value: 'all',
                },
              ],
            },
          },
          status: {
            type: 'in' as const,
            label: i18n.t('it_management.device_catalog.orders.filter_statuses'),
            options: {
              options: [
                {
                  label: i18n.t('it_management.device_catalog.orders.filter_statuses'),
                  value: 'all',
                },
                { label: 'pending', value: 'pending' },
                { label: 'authorized', value: 'authorized' },
                { label: 'paid', value: 'paid' },
                { label: 'refunded', value: 'refunded' },
                { label: 'partially_refunded', value: 'partially_refunded' },
                { label: 'cancelled', value: 'cancelled' },
              ],
            },
          },
          type: {
            type: 'in' as const,
            label: i18n.t('it_management.device_catalog.orders.filter_types'),
            options: {
              options: [
                { label: i18n.t('it_management.device_catalog.orders.filter_types'), value: 'all' },
              ],
            },
          },
        },
        sortings: {
          createdAt: {
            label: i18n.t('it_management.device_catalog.orders.column_date'),
            sortBy: 'createdAt',
          },
          status: {
            label: i18n.t('it_management.device_catalog.orders.column_status'),
            sortBy: 'status',
          },
          itemsCount: {
            label: i18n.t('it_management.device_catalog.orders.column_items'),
            sortBy: 'itemsCount',
          },
          price: {
            label: i18n.t('it_management.device_catalog.orders.column_total'),
            sortBy: 'price',
          },
        },
        defaultSortings: { field: 'createdAt' as const, order: 'desc' as const },
      }),
      [companyId, marketplaceClient]
    ),
    [companyId, marketplaceClient]
  )

  return source
}
